(ns ductile.ui.fields
  (:require ["scroll-into-view-if-needed" :as scrollIntoView]
            [clojure.pprint :as pprint]
            [clojure.string :as str]
            [ductile.locations.delivery-modalities :as dm]
            [ductile.time.format :as time-format]
            [ductile.ui.components :as ui]
            [ductile.ui.components.combobox :as combobox]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.locale :refer [tr]]
            [ductile.ui.util :as util]
            [ductile.util]
            [nextjournal.clerk.render :as render]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.clerk.viewer :as viewer]
            [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Delivery modalities: free-text textarea with live-parsed readonly preview
;; ---------------------------------------------------------------------------

(defn- parsed-field
  "A single parsed field row. Flashes when `highlight?` is true."
  [highlight? label-view value-view]
  [:div.flex.gap-2.mt-1.rounded.px-1.transition-colors.duration-700
   {:class (if highlight? "bg-indigo-100" "bg-transparent")}
   label-view
   value-view])

(defn- parsed-preview
  "Shows parsed delivery hours, night delivery, and notes as readonly fields."
  [{:location/keys [delivery-hours night-delivery saturday-delivery]} highlighted]
  [:div.mt-2.rounded-md.bg-slate-50.border.border-slate-200.px-3.py-2.text-sm
   [:div.text-xs.font-semibold.text-slate-500.mb-1 (tr "Parsed")]
   (if delivery-hours
     [parsed-field (contains? highlighted :location/delivery-hours)
      [:span.text-slate-500 (tr :location/delivery-hours) ":"]
      [:span.font-medium delivery-hours]]
     [:div.text-slate-400.text-xs (tr "No delivery hours detected")])
   (when night-delivery
     [parsed-field (contains? highlighted :location/night-delivery)
      [:span.text-slate-500 (tr :location/night-delivery) ":"]
      [:span.font-medium (tr night-delivery)]])
   (when saturday-delivery
     [parsed-field (contains? highlighted :location/saturday-delivery)
      [:span.text-slate-500 (tr :location/saturday-delivery) ":"]
      [:span.font-medium (tr saturday-delivery)]])])

(defn delivery-modalities-field-view
  "Free-text textarea for :location/delivery-modalities with a live-parsed
  readonly preview showing structured delivery hours, night delivery, and notes."
  [field get-value-fn recorded-attr-path-fn]
  (let [path (recorded-attr-path-fn field)
        value (or (get-value-fn field) "")
        parsed (dm/parse value)
        !prev-parsed (hooks/use-ref nil)
        !highlighted (hooks/use-state #{})
        !timer (hooks/use-ref nil)]
    ;; Diff previous vs current parsed, highlight changed keys.
    ;; Use pr-str as dep so effect only fires on value change (maps aren't reference-equal).
    (let [parsed-str (pr-str parsed)]
      (hooks/use-effect
       (fn []
         (let [prev @!prev-parsed
               changed (into #{}
                             (filter (fn [k] (not= (get prev k) (get parsed k))))
                             [:location/delivery-hours :location/night-delivery :location/saturday-delivery])]
           (reset! !prev-parsed parsed)
           (when (and prev (seq changed))
             ;; Clear any pending timer
             (when-let [t @!timer] (js/clearTimeout t))
             (reset! !highlighted changed)
             (reset! !timer (js/setTimeout #(reset! !highlighted #{}) 800)))))
       [parsed-str]))
    [:div.py-1
     [:textarea.w-full.rounded-md.border.border-slate-300.px-3.py-2.text-sm.resize-none
      {:rows 4
       :value value
       :placeholder (tr "e.g. %1" "Anlieferzeiten: Mo-Fr 08:00-18:00")
       :on-change #(deframe/route-assoc-in! path (.. % -target -value))}]
     (when (and (not (str/blank? value)) (seq parsed))
       [parsed-preview parsed @!highlighted])]))

(defn section-header [text]
  [:div.text-sm.font-bold.ml-4.text-slate-600.mt-3 text])

(defn section [header & content]
  (let [items (filter some? content)]
    (if (seq items)
      (into [:<> [section-header header]] items)
      [:<>])))

(defn datetime-fmt? [s]
  (re-matches #"\d{4}-\d{2}-\d{2}T\d{2}:\d{2}" s))

(defn date-fmt? [s]
  (re-matches #"\d{4}-\d{2}-\d{2}" s))

(defn week-fmt? [s]
  (re-matches #"\d{4}-W\d{2}" s))

(defn month-fmt? [s]
  (re-matches #"\d{4}-\d{2}" s))

(def ignored-attr
  {:label (tr "ignored")
   :ignored? true
   :value nil
   :selected? nil?})

(def present-attr
  {:label (tr "present")
   :value {}
   :selected? #(and (map? %) (empty? %))
   :highlight-preview? true})

(def missing-attr
  {:label (tr "missing")
   :value :ductile.vehicle-search/missing
   :selected? #{:ductile.vehicle-search/missing}
   :highlight-preview? true})

(defn format-timestamp [timestamp]
  (if (datetime-fmt? timestamp)
    (time-format/->str (js/Date. timestamp))
    timestamp))

(def at-attr
  {:type :datetime
   :label (tr "at timestamp")
   :set-value (fn [_ val] {:ductile.vehicle-search/at (or val "")})
   :input-value :ductile.vehicle-search/at
   :preview (fn [{:keys [label input-value]} v]
              [:span label " " [:span.font-bold (format-timestamp (input-value v))]])
   :selected? #(contains? % :ductile.vehicle-search/at)
   :highlight-preview? true
   :validate (fn [{:keys [input-value]} v]
               (let [date (input-value v)]
                 (when (or (str/blank? date) (not (datetime-fmt? date)))
                   {:message (tr "Requires a timestamp like %1." (format-timestamp "2024-09-30T13:50"))})))})

(def on-attr
  {:type :date
   :label (tr "on")
   :set-value (fn [_ val] {:ductile.vehicle-search/on (or val "")})
   :input-value :ductile.vehicle-search/on
   :preview (fn [{:keys [label input-value]} v]
              [:span label " " [:span.font-bold (input-value v)]])
   :selected? #(contains? % :ductile.vehicle-search/on)
   :highlight-preview? true
   :validate (fn [{:keys [input-value]} v]
               (let [date (input-value v)]
                 (when (or (str/blank? date) (not (date-fmt? date)))
                   {:message (tr "Requires a date like 2024-09-04.")})))})

(def in-week-attr
  {:type :week
   :label (tr "in week")
   :set-value (fn [_ val] {:ductile.vehicle-search/in-week (or val "")})
   :input-value :ductile.vehicle-search/in-week
   :preview (fn [{:keys [label input-value]} v]
              [:span label " " [:span.font-bold (input-value v)]])
   :selected? #(contains? % :ductile.vehicle-search/in-week)
   :highlight-preview? true
   :validate (fn [{:keys [input-value]} v]
               (let [date (input-value v)]
                 (when (or (str/blank? date) (not (week-fmt? date)))
                   {:message (tr "Requires a week like 2024-W05.")})))})

(def in-month-attr
  {:type :month
   :label (tr "in month")
   :set-value (fn [_ val] {:ductile.vehicle-search/in-month (or val "")})
   :input-value :ductile.vehicle-search/in-month
   :preview (fn [{:keys [label input-value]} v]
              [:span label " " [:span.font-bold (input-value v)]])
   :selected? #(contains? % :ductile.vehicle-search/in-month)
   :highlight-preview? true
   :validate (fn [{:keys [input-value]} v]
               (let [date (input-value v)]
                 (when (or (str/blank? date) (not (month-fmt? date)))
                   {:message (tr "Requires a month like 2024-03.")})))})

(def before-after-attr
  {:type :date-range
   :label [(tr "between")  (tr "and")]
   :set-value (fn [{:ductile.vehicle-search/keys [before after]} {:keys [start end]}]
                {:ductile.vehicle-search/before (or end before "")
                 :ductile.vehicle-search/after (or start after "")})
   :commit-value (fn [{:ductile.vehicle-search/keys [before after]}]
                   (cond-> {}
                     (not (str/blank? before)) (assoc :ductile.vehicle-search/before before)
                     (not (str/blank? after)) (assoc :ductile.vehicle-search/after after)))
   :input-value [:ductile.vehicle-search/after :ductile.vehicle-search/before]
   :preview (fn [{:keys [label input-value]} v]
              (let [start ((first input-value) v)
                    end ((last input-value) v)]
                (cond
                  (and start end) [:span
                                   (first label) " " [:span.font-bold start]
                                   " "
                                   (last label) " " [:span.font-bold end]]
                  (and start (not end)) [:span (tr "after") " " [:span.font-bold start]]
                  (and end (not start)) [:span (tr "before") " " [:span.font-bold end]])))
   :selected? #(or (contains? % :ductile.vehicle-search/before) (contains? % :ductile.vehicle-search/after))
   :highlight-preview? true
   :validate (fn [{:keys [input-value]} v]
               (when (and (not (date-fmt? ((first input-value) v))) (not (date-fmt? ((last input-value) v))))
                 {:message (tr "Requires a complete date for at least one field.")}))})

(defn present-text-attr [options]
  (merge {:type :text
          :label (tr "present")
          :set-value (fn [selected-value v]
                       (if (string? selected-value)
                         (or v selected-value)
                         ""))
          :preview (fn [{:keys [label]} v]
                     (if (str/blank? v)
                       (tr "unspecified")
                       v))
          :selected? string?
          :highlight-preview? true}
         options))

(defn present-multi-select-attr [choices]
  {:type :multi-select
   :label (tr "present")
   :choices choices
   :set-value (fn [selected-values v]
                (if (set? selected-values)
                  (if (nil? v) selected-values (conj selected-values v))
                  #{}))
   :preview (fn [{:keys [label]} v]
              (if (seq v) (str/join ", " (map tr v)) (tr label)))
   :selected? set?
   :highlight-preview? true})

(defn exclude-multi-select-attr [choices]
  (let [unwrap :ductile.vehicle-search/not
        wrap (fn [x] {:ductile.vehicle-search/not x})]
    {:type :multi-select
     :label (tr "excluding")
     :choices choices
     :set-value (fn [selected-values v]
                  (let [exclusion-vals (unwrap selected-values)]
                    (if (set? exclusion-vals)
                      (if (nil? v)
                        selected-values
                        (wrap (conj exclusion-vals v)))
                      (wrap #{}))))
     :remove-value (fn [selected-values v]
                     (wrap (disj (unwrap selected-values) v)))
     :->options unwrap
     :preview (fn [{:keys [label]} v]
                (if (seq (:ductile.vehicle-search/not v))
                  (tr "excluding: %1" (str/join ", " (:ductile.vehicle-search/not v)))
                  label))
     :commit-value (fn [v] (when (seq (unwrap v)) v))
     :selected? unwrap
     :highlight-preview? true}))

(defn present-fields-attr [fields]
  {:type :fields
   :label (tr "present")
   :fields fields
   :preview (fn [{:keys [label fields]} selected-value]
              (if (and (map? selected-value) (seq selected-value))
                [:table.text-xs.mt-2
                 (reduce (fn [acc {:keys [path label]}]
                           (if-let [v (get-in selected-value path)]
                             (conj acc [:tr
                                        [:th.text-right.pr-2 (tr label) ":"]
                                        [:td (if (coll? v)
                                               (str/join ", " (map tr v))
                                               (tr v))]])
                             acc))
                         [:tbody]
                         fields)]
                (tr label)))
   :set-value (fn [transport v]
                (if (nil? v) {} (merge transport v)))
   :selected? map?
   :highlight-preview? true})

(defn present-transport-attr [k option-values]
  (present-fields-attr [{:path [:transport/name]
                         :label (tr "Transport name")
                         :placeholder (tr "e.g. %1" "SEINE HGW")
                         :choices (get-in option-values [k :transport/name])}
                        {:path [:transport/mode]
                         :label (tr "Transport mode")
                         :placeholder (tr "e.g. %1" "truck")
                         :choices (get-in option-values [k :transport/mode])}
                        {:path [:load/id]
                         :label (tr "Load ID")
                         :placeholder (tr "e.g. %1" "LL-DU-12345")
                         #_#_:choices (get-in option-values [k :load/id])}
                        {:path [:transport/load-number]
                         :label (tr "Transport load number")
                         :placeholder (tr "e.g. %1" "X03489 SEHI23/1230720")
                         #_#_:choices (get-in option-values [k :transport/load-number])}
                        {:path [:transport/customer-load-number]
                         :label (tr "Customer Load Number")
                         :placeholder (tr "e.g. %1" "530618")
                         #_#_:choices (get-in option-values [k :transport/load-number])}
                        {:path [:origin/location :address/name]
                         :label (tr "Origin location")
                         :placeholder (tr "e.g. %1" "Dingolfing")
                         #_#_:choices (get-in option-values [k :origin/location :address/name])}
                        #_{:path [:origin/location :compound/name]
                           :label (tr "origin compound")
                           :placeholder (tr "e.g. %1" "")}
                        {:path [:destination/location :address/name]
                         :label (tr :destination/location)
                         :placeholder (tr "e.g. %1" "Pyongtaek")
                         #_#_:choices (get-in option-values [k :destination/location :address/name])}
                        #_{:path [:destination/location :compound/name]
                           :label (tr "destination compound")
                           :placeholder (tr "e.g. %1" "")}
                        {:path [:takeover/conditions]
                         :label (tr :takeover/conditions)
                         :placeholder (tr "e.g. %1" (tr :takeover.condition/darkness))
                         :choices (get-in option-values [k :takeover/conditions])}]))

(defn present-parking-attr [k option-values]
  (present-fields-attr [{:path [:parking-spot/zone]
                         :label (tr "Zone")
                         :placeholder (tr "e.g. %1" "PDI")
                         :choices (:parking-zone/name option-values)}]))

(def default-presence-options
  [ignored-attr missing-attr present-attr])

(def default-datetime-presence-options
  [ignored-attr missing-attr present-attr at-attr on-attr in-week-attr in-month-attr before-after-attr])

(defn box [& content]
  (let [options (first content)
        options? (map? options)
        editing? (and options? (:editing? options))
        focused? (and options? (:focused? options))
        highlight-preview? (and options? (:highlight-preview? options))
        editing-rounded-class (:editing-rounded-class options "rounded-t-lg")
        !el (hooks/use-ref nil)]
    (hooks/use-effect #(when focused?
                         (scrollIntoView @!el #js {:behavior "smooth"
                                                   :scrollMode "if-needed"
                                                   :block "nearest"})) [focused?])
    [:div.px-4.py-3.relative.w-full.cursor-pointer.bg-slate-100
     (merge {:ref !el
             :class (str (if editing? (str editing-rounded-class " border border-slate-300 ") "rounded-lg ")
                         (when-not (false? (:show-editable? options)) "justify-between flex items-center ")
                         (cond
                           (and (not editing?) focused?) "border-2 border-indigo-600 "
                           (and (not editing?) highlight-preview?) "border-2 border-orange-500 "
                           (and (not editing?) (not focused?)) "border-2 border-slate-200 "))}
            (when options? (dissoc options :editing? :focused? :highlight-preview? :show-editable? :editing-rounded-class)))
     (when highlight-preview?
       [:div.absolute.right-0.top-0.rounded-bl.rounded-tr.bg-orange-500.w-3.h-3.flex.items-center.justify-center.text-white
        [ui/icon {:size 10} :check]])
     (into [:div] (if options? (rest content) content))
     (when-not (false? (:show-editable? options))
       [:button.cursor-pointer
        {:class (str "text-[11px] "
                     (if highlight-preview?
                       "text-orange-600 hover:text-orange-700 "
                       "text-indigo-600 hover:text-indigo-700 "))}
        (tr "Edit")])]))

(defn label [& content]
  (into [:label.text-sm.font-bold] content))

(defn hint [& content]
  (into [:div.text-xs.text-slate-600] content))

(defn debug-label [k]
  [:span " "
   [:span.font-mono.text-xs.bg-slate-200.rounded
    {:class "px-1 py-[2px]"}
    (str k)]])

(defn commit-value! [!m k v]
  (swap! !m (fn [m]
              (cond
                (str/blank? v) (dissoc m k)
                (vector? k) (assoc-in m k v)
                :else (assoc m k v)))))

(defn error-message [{:keys [message]}]
  [:div.text-red-500.font-bold.text-xs message])

(defn textarea [!m k {:as opts :keys [commit-fn placeholder on-change highlight-presence?]}]
  (let [!state (ui/use-field-state (:label opts) {})
        !textarea (hooks/use-ref nil)
        {:keys [editing? focused?]} @!state
        v (get @!m k)]
    (hooks/use-effect #(when editing? (.focus @!textarea)) [editing?])
    [box
     {:show-editable? false
      :editing? editing?
      :focused? focused?
      :highlight-preview? (and highlight-presence? (not (str/blank? v)))
      :editing-rounded-class "rounded-lg"}
     [label (:label opts) (when (ui/debug-mode?) [debug-label k])]
     [:textarea.rounded-md.p-2.text-sm.w-full.resize-none.text-black
      (merge {:default-value (or v "")
              :ref !textarea
              :on-blur #(do ((or commit-fn commit-value!) !m k (:selected-value @!state))
                            (ui/quit-editing-field!))
              :on-focus #(when-not editing? (ui/edit-field! (:label opts)))
              :on-change (fn [event]
                           (swap! !state assoc :selected-value (.. event -target -value))
                           (when on-change (on-change event)))
              :on-key-down (fn [event]
                             (when editing?
                               (cond
                                 (= (.-key event) "Escape")
                                 (do (.preventDefault event) (.blur @!textarea))
                                 (or (and (.-altKey event) (= (.-key event) "Enter"))
                                     (= (.-key event) "Tab"))
                                 (do (.preventDefault event)
                                     (.stopPropagation event)
                                     (.blur @!textarea)
                                     (ui/focus-field! ui/next-val)))))}
             (dissoc opts :label :hint :on-change :highlight-presence?))]
     (when-let [h (:hint opts)]
       [:div.flex.justify-between.items-center
        [hint h]
        (when editing?
          [:div.text-xs.flex.items-center.gap-2
           [:div.relative {:class "top-[0px]"}
            [ui/keys-hint (if ui/is-mac? "⌥" "Alt") "↩"]]
           [hint (tr "to save")]])])]))

(defmulti nested-field :type)

(defmethod nested-field :datetime [{:keys [!state input-value set-value selected? label]}]
  (let [{:keys [selected-value selected-option error]} @!state
        {:keys [validate]} selected-option
        selected? (selected? selected-value)
        !el (hooks/use-ref nil)
        initial-value (if selected? (input-value selected-value) "")
        set-value! #(swap! !state assoc :selected-value (set-value selected-value (.. % -target -value)))]
    (hooks/use-effect #(when selected?
                         (set! (.-value @!el) initial-value)
                         (.focus @!el)) [selected?])
    [:div.flex.items-center.gap-2
     [:label label]
     [:input.rounded-md.p-1.text-xs.disabled:bg-slate-100.text-black.border.shadow
      {:type "datetime-local"
       :class (if (and selected? error) "border-red-500" "border-slate-300")
       :ref !el
       :default-value initial-value
       :disabled (not selected?)
       :on-key-down #(when (= (.-key %) "Enter") (set-value! %))
       :on-blur #(when selected? (set-value! %))}]
     (when (and selected? error)
       [error-message error])]))

(defmethod nested-field :date [{:keys [!state input-value set-value selected? label]}]
  (let [{:keys [selected-value selected-option error]} @!state
        {:keys [validate]} selected-option
        selected? (selected? selected-value)
        !el (hooks/use-ref nil)
        initial-value (if selected? (input-value selected-value) "")
        set-value! #(swap! !state assoc :selected-value (set-value selected-value (.. % -target -value)))]
    (hooks/use-effect #(when selected?
                         (set! (.-value @!el) initial-value)
                         (.focus @!el)) [selected?])
    [:div.flex.items-center.gap-2
     [:label label]
     [:input.rounded-md.p-1.text-xs.disabled:bg-slate-100.text-black.border.shadow
      {:type "date"
       :class (if (and selected? error) "border-red-500" "border-slate-300")
       :ref !el
       :default-value initial-value
       :disabled (not selected?)
       :on-key-down #(when (= (.-key %) "Enter") (set-value! %))
       :on-blur #(when selected? (set-value! %))}]
     (when (and selected? error)
       [error-message error])]))

(defmethod nested-field :week [{:keys [!state input-value set-value selected? label]}]
  (let [{:keys [selected-value selected-option error]} @!state
        {:keys [validate]} selected-option
        selected? (selected? selected-value)
        !el (hooks/use-ref nil)
        initial-value (if selected? (input-value selected-value) "")
        set-value! #(swap! !state assoc :selected-value (set-value selected-value (.. % -target -value)))]
    (hooks/use-effect #(when selected?
                         (set! (.-value @!el) initial-value)
                         (.focus @!el)) [selected?])
    [:div.flex.items-center.gap-2
     [:label label]
     [:input.rounded-md.p-1.text-xs.disabled:bg-slate-100.text-black.border.shadow
      {:type "week"
       :class (if (and selected? error) "border-red-500" "border-slate-300")
       :ref !el
       :default-value initial-value
       :disabled (not selected?)
       :on-key-down #(when (= (.-key %) "Enter") (set-value! %))
       :on-blur #(when selected? (set-value! %))}]
     (when (and selected? error)
       [error-message error])]))

(defmethod nested-field :month [{:keys [!state input-value set-value selected? label]}]
  (let [{:keys [selected-value selected-option error]} @!state
        {:keys [validate]} selected-option
        selected? (selected? selected-value)
        !el (hooks/use-ref nil)
        initial-value (if selected? (input-value selected-value) "")
        set-value! #(swap! !state assoc :selected-value (set-value selected-value (.. % -target -value)))]
    (hooks/use-effect #(when selected?
                         (set! (.-value @!el) initial-value)
                         (.focus @!el)) [selected?])
    [:div.flex.items-center.gap-2
     [:label label]
     [:input.rounded-md.p-1.text-xs.disabled:bg-slate-100.text-black.border.shadow
      {:type "month"
       :class (if (and selected? error) "border-red-500" "border-slate-300")
       :ref !el
       :default-value initial-value
       :disabled (not selected?)
       :on-key-down #(when (= (.-key %) "Enter") (set-value! %))
       :on-blur #(when selected? (set-value! %))}]
     (when (and selected? error)
       [error-message error])]))

(defn format-date
  "Format a JS date in UTC as YYYY-MM-DD"
  [d]
  (subs (.toISOString d) 0 (count "YYYY-MM-DD")))

(defn start-of-week
  "Given a week in the <input type=\"week,> format (e.g. \"2024-W01\"),
  return the first day of that week as a js/Date"
  [week]
  (let [[year week] (map parse-long (str/split week #"-W"))]
    (when
     (or (< week 1) (> week 53))
      (throw (js/RangeError. "ISO 8601 weeks are numbered from 1 to 53")))
    (let [simple (js/Date. (.UTC js/Date year 0 (+ 1 (* (- week 1) 7))))
          day-of-week (.getDay simple)
          iso-week-start (doto (js/Date. (.getTime simple))
                           (.setDate (+ 1 (- (.getDate simple) day-of-week))))]
      ;;Get the Monday past, and add a week if the day was Friday, Saturday or Sunday.
      (when (> day-of-week 4)
        (.setDate iso-week-start (+ 7 (.getDate iso-week-start))))

      ;;The latest possible ISO week starts on December 28 of the current year.
      (if (or (> (.getFullYear iso-week-start) year)
              (and (= (.getFullYear iso-week-start) year)
                   (= (.getMonth iso-week-start) 11)
                   (> (.getDate iso-week-start) 28)))
        (throw (js/RangeError. (str year " has no ISO week " week)))
        iso-week-start))))

#_(format-date (start-of-week "2023-W01"))
#_(format-date (start-of-week "2023-W52"))

(defn end-of-week
  "Given a week in the <input type=\"week,> format (e.g. \"2024-W01\"),
  return the last day of that week as a js/Date"
  [week]
  (let [start (start-of-week week)]
    (js/Date. (.setDate start (+ 6 (.getDate start))))))

#_(format-date (end-of-week "2023-W01"))
#_(format-date (end-of-week "2023-W52"))

(defn start-of-month [d]
  (js/Date. (.setDate (js/Date. d) 1)))

(defn end-of-month [d]
  (let [month (.getMonth d)]
    (js/Date. (doto (js/Date. d)
                ;;next month
                (.setMonth (inc month))
                ;;last day of previous month
                (.setDate 0)))))

(defmethod nested-field :date-range [{:keys [!state input-value set-value selected? label]}]
  (let [{:keys [selected-value error]} @!state
        selected? (selected? selected-value)
        start (get selected-value (first input-value))
        end (get selected-value (last input-value))
        !start-el (hooks/use-ref nil)
        !end-el (hooks/use-ref nil)
        initial-start-value (if selected? start "")
        initial-end-value (if selected? end "")
        set-start-value! #(swap! !state assoc :selected-value (set-value selected-value {:start (.. % -target -value)}))
        set-end-value! #(swap! !state assoc :selected-value (set-value selected-value {:end (.. % -target -value)}))]
    (hooks/use-effect #(when selected?
                         (set! (.-value @!start-el) initial-start-value)
                         (set! (.-value @!end-el) initial-end-value)
                         (.focus @!start-el)) [selected?])
    [:div
     [:div.flex.items-center.gap-2
      [:label (first label)]
      [:input.rounded-md.p-1.text-xs.disabled:bg-slate-100.text-black.border.shadow
       {:type "date"
        :class (if (and selected? error) "border-red-500" "border-slate-300")
        :ref !start-el
        :default-value initial-start-value
        :disabled (not selected?)
        :on-key-down #(when (= (.-key %) "Enter") (set-start-value! %))
        :on-blur #(when selected? (set-start-value! %))}]
      [:label (last label)]
      [:input.rounded-md.p-1.text-xs.disabled:bg-slate-100.text-black.border.shadow
       {:type "date"
        :class (if (and selected? error) "border-red-500" "border-slate-300")
        :ref !end-el
        :default-value initial-end-value
        :disabled (not selected?)
        :on-key-down #(when (= (.-key %) "Enter") (set-end-value! %))
        :on-blur #(when selected? (set-end-value! %))}]]
     (when (and selected? error)
       [:div.mt-1
        [error-message error]])]))

(defn multi-select [{:keys [choices placeholder selected-value disabled? on-change on-remove show-selected? ->options label on-click]}]
  [:div
   [:div.w-fit
    [combobox/view
     {:data-testid (str label "-multi-select-filter-input")
      :choices (map combobox/->choice choices)
      :placeholder placeholder
      :disabled disabled?
      :width :lg
      :clear-on-change? true
      :on-change (fn [{% :ui/value}]
                   (on-change (if (keyword? (first choices)) (keyword (namespace (first choices)) %) %)))
      :on-click (or on-click
                    #(.stopPropagation %))}]]
   [:div
    (when (and show-selected? (seq selected-value))
      (into
       [:div.flex.flex-wrap.gap-2.mt-1 {:on-click #(.stopPropagation %)}]
       (map (fn [x]
              [:div.bg-white.shadow-sm.border.flex.rounded-md.overflow-hidden.border-slate-300
               [:div {:class "text-[12px] pl-[10px] pr-[7px] py-[2px]"} (tr x)]
               [:button.text-slate-600.hover:text-indigo-700.hover:bg-indigo-50.border-l
                {:on-click #(on-remove x)
                 :class "px-[5px] py-[2px]"}
                [:svg {:xmlns "http://www.w3.org/2000/svg" :fill "none" :viewBox "0 0 24 24" :stroke-width "1.5" :stroke "currentColor" :class "w-[12px] h-[12px]"}
                 [:path {:stroke-linecap "round" :stroke-linejoin "round" :d "M6 18L18 6M6 6l12 12"}]]]]))
       (cond-> selected-value ->options ->options)))]])

(defmethod nested-field :text [{:as field :keys [!state placeholder set-value selected? label input-mask]}]
  (let [{:keys [selected-value]} @!state
        selected? (selected? selected-value)]
    [:div.flex.gap-2.items-center
     [:label label]
     [ui/input (cond-> {:value selected-value
                        :disabled? (not selected?)
                        :show-selected? selected?
                        :on-change #(swap! !state assoc :selected-value (set-value selected-value (.. % -target -value)))
                        :on-clear (fn [event]
                                    (.stopPropagation event)
                                    (.preventDefault event)
                                    (swap! !state assoc :selected-value (set-value selected-value "")))}
                 input-mask (assoc :mask input-mask))]]))

(defmethod nested-field :multi-select [{:as field :keys [!state choices placeholder set-value remove-value selected? label on-click]}]
  (let [{:keys [selected-value]} @!state
        selected? (selected? selected-value)]
    [:div.flex.gap-2
     [:label label]
     [multi-select (assoc field
                          :selected-value selected-value
                          :disabled? (not selected?)
                          :show-selected? selected?
                          :on-click on-click
                          :on-change #(swap! !state assoc :selected-value (set-value selected-value %))
                          :on-remove #(swap! !state update :selected-value (or remove-value disj) %))]]))

(defmethod nested-field :fields [{:keys [!state selected? label fields]}]
  (let [{:keys [selected-value]} @!state
        selected? (selected? selected-value)]
    [:div.flex.flex-col.gap-2
     [:label label]
     (when selected?
       (into [:table.w-full {:on-click #(.stopPropagation %)}]
             (map (fn [{:as field :keys [label path placeholder]}]
                    (let [current-value (get-in selected-value path)
                          full-path (vec (concat [:selected-value] path))]
                      [:tr
                       [:td.py-2.pr-3
                        [:label.text-xs label]]
                       [:td.py-2
                        (if (:choices field)
                          [multi-select (assoc field
                                               :selected-value current-value
                                               :show-selected? true
                                               :on-change (fn [selected]
                                                            (swap! !state #(assoc-in %
                                                                                     full-path
                                                                                     (if (set? current-value)
                                                                                       (if (nil? selected)
                                                                                         current-value
                                                                                         (conj current-value selected))
                                                                                       #{selected}))))
                                               :on-remove #(swap! !state update-in full-path disj %))]
                          [ui/input
                           {:placeholder placeholder
                            :value current-value
                            :on-change (fn [event]
                                         (let [text (.. event -target -value)]
                                           (swap! !state (fn [state]
                                                           (-> (assoc-in state full-path text)
                                                               (update-in (vec (drop-last full-path))
                                                                          ductile.util/dissoc-blanks-vals))))))}])]])))
             fields))]))

(defmethod nested-field :default [{:keys [label]}]
  [:div label])

(defn debug-requirement-hint [text]
  [:div.rounded-lg.p-3.bg-red-100.text-red-700.text-xs.font-mono.mt-3.border.border-red-200
   "❗ "
   text])

(defn list-select-item [!state {:as field :keys [list-index selected? value set-value ignored?]}]
  (let [{:keys [selected-value]} @!state
        selected? (selected? selected-value)
        select! #(swap! !state (fn [state]
                                 (-> state
                                     (assoc :selected-option field)
                                     (assoc :selected-value (if set-value (set-value selected-value nil) value))
                                     (dissoc :error))))
        selection-key (str (inc list-index))]
    (hooks/use-effect (fn []
                        (let [on-key-down (fn [event]
                                            (when (and (.-altKey event) (= (.-code event) (str "Digit" selection-key)))
                                              (.preventDefault event)
                                              (select!)))]
                          (js/addEventListener "keydown" on-key-down)
                          #(js/removeEventListener "keydown" on-key-down))))
    [:div.flex.px-3.py-2.gap-2.cursor-pointer.text-sm.transition-all.border-b.relative
     {:class (when selected? "bg-indigo-50 ")
      :on-click select!}
     [:div.rounded-full.bg-white.flex.items-center.justify-center.border.border-slate-300.shadow.shrink-0
      {:class "w-[20px] h-[20px]"}
      (when selected?
        [:div.rounded-full.bg-slate-800 {:class "w-[10px] h-[10px]"}])]
     [:div
      [nested-field (assoc field :!state !state :on-click select!)]
      (when (and (not ignored?) (ui/debug-mode?) (not set-value) (not value))
        [debug-requirement-hint
         "Either :value or :set-value is required but neither is present."])]
     [:div.absolute.right-1.top-1
      [ui/keys-hint (if ui/is-mac? "⌥" "Alt") selection-key]]]))

(defn edit-panel [{:keys [title on-close on-confirm !state]} content]
  (let [save+quit! #(when (on-confirm) (on-close))]
    [:<>
     [ui/key-down-handler {"Enter" save+quit!}]
     [:div.bg-white.shadow-xl.rounded-lg.relative.outline.outline-slate-400
      [:h2.font-bold.text-sm.mb-0.px-4.py-3 title]
      [:button.rounded-md.bg-indigo-600.hover:bg-indigo-500.shadow.text-white.cursor-pointer.absolute.right-1.top-1.flex.items-center.justify-center
       {:class "w-[24px] h-[24px] "}
       [ui/icon {:size 16 :on-click on-close} :x]]
      (into [:div.flex.flex-col.flex-auto.border-t] content)
      [:div
       (when (ui/debug-mode?)
         [:div.bg-slate-100.px-4.py-3.text-slate-900.overflow-x-auto.border-b.shadow-inner
          [render/inspect-presented {:!expanded-at (r/atom {[] true})} (viewer/present @!state)]])
       [:div.p-3.flex.gap-2
        [:button.rounded-lg.bg-indigo-600.hover:bg-indigo-500.transition-all.p-2.text-sm.text-center.font-bold.flex-auto.text-white.disabled:opacity-20.flex.items-center.justify-center.gap-2
         {:on-click save+quit!}
         (tr "Apply")
         [ui/keys-hint {:dark? true} "↩"]]
        [:button.rounded-lg.bg-indigo-600.hover:bg-indigo-500.transition-all.p-2.text-sm.text-center.font-bold.text-white.disabled:opacity-20.flex.items-center.gap-2.justify-center
         {:on-click on-close :class "w-[150px]"}
         (tr "Close")
         [ui/keys-hint {:dark? true} "ESC"]]]]]]))

(defn list-select [!m k {:as opts :keys [options commit-fn]}]
  (let [!el (hooks/use-ref nil)
        !state (ui/use-field-state (:label opts) {:selected-value (get @!m k) :editing? false})
        v (get @!m k)
        {:keys [editing? focused?]} @!state
        {:as selected-option :keys [preview highlight-preview?]} (first (filter (fn [{:keys [selected?]}] (selected? v)) options))]
    (hooks/use-effect #(when editing?
                         (scrollIntoView @!el #js {:behavior "smooth"
                                                   :scrollMode "if-needed"
                                                   :block "nearest"})) [editing?])
    ;; TODO: This is only necessary now because `(:selected-value !state)` is nil
    ;; initially and is only later set when `on-load-form-params` is processed in
    ;; `::on-load`. Hence, `!state` must reflect the new `form-params` as well.
    (hooks/use-effect #(swap! !state assoc :selected-value v) [v])
    [:div
     {:ref !el}
     (if editing?
       [:<>
        [ui/key-down-handler {"Escape" #(ui/quit-editing-field!)
                              "Tab" (partial ui/quit+focus-next-field! (:label opts))}]
        [edit-panel
         {:title [label "✏️ " (:label opts) (when (ui/debug-mode?) [debug-label k])]
          :on-close #(ui/quit-editing-field!)
          :on-confirm (fn []
                        (let [{:keys [selected-option selected-value]} @!state
                              {:keys [validate commit-value]} selected-option]
                          (if-let [error (when (fn? validate) (validate selected-option selected-value))]
                            (do (swap! !state assoc :error error) false)
                            (do (swap! !state dissoc :error)
                                ((or commit-fn commit-value!) !m k (if (fn? commit-value)
                                                                     (commit-value selected-value)
                                                                     selected-value))
                                true))))
          :!state !state}
         (map-indexed (fn [i {:as o :keys [value]}]
                        [list-select-item !state (assoc o :list-index i)])
                      options)]]
       [box
        {:on-click #(ui/toggle-editing-field! (:label opts))
         :editing? editing?
         :focused? focused?
         :highlight-preview? highlight-preview?}
        [label (:label opts) (when (ui/debug-mode?) [debug-label k]) ": "]
        [:span.text-sm
         {:class (when highlight-preview? "text-orange-600 ")}
         (if (fn? preview)
           [preview selected-option v]
           (:label selected-option))]])]))

(defn tab-button [!state k {:as t :keys [label tab selection-key selected? on-click use-keys?] :or {use-keys? true}}]
  (let [select! (fn [event]
                  (.preventDefault event)
                  (when on-click (on-click !state t))
                  (swap! !state assoc k tab))]
    (when use-keys?
      (hooks/use-effect (fn []
                          (let [on-key-down (fn [event]
                                              (when (and (:editing? @!state) (.-altKey event) (= (.-code event) (str "Digit" selection-key)))
                                                (select! event)))]
                            (js/addEventListener "keydown" on-key-down)
                            #(js/removeEventListener "keydown" on-key-down)))))
    [:button.font-bold.first:rounded-l-full.last:rounded-r-full.px-3.py-1.flex.items-center.border-r.last:border-r-0
     {:on-click select!
      :class (if selected? "bg-indigo-600 text-white bor" "bg-white hover:bg-indigo-50 text-indigo-600 ")}
     [:span.mr-2 label]
     (when use-keys?
       [ui/keys-hint {:dark? selected?} (if ui/is-mac? "⌥" "Alt") selection-key])]))

(defn tabs [!state k ts]
  (let [current-tab (get @!state k)]
    (into [:div.flex.items-center.rounded-full.overflow-hidden.border.border-slate-300 {:class "text-[12px]"}]
          (map-indexed (fn [i {:as t :keys [label tab]}]
                         (let [selected? (= current-tab tab)]
                           [tab-button !state k (assoc t :selected? selected? :selection-key (inc i))])))
          ts)))

(def !trucks
  (deframe/!get-in [:trucks :result] []))

(def !carriers
  (deframe/!get-in [:carriers :result] []))

(def !current-load
  (deframe/!route-get :current-load))

(def reset-current-load
  [:op/dissoc-in [:current-route/transient :current-load]])

(defn process-ref [attr ref] [attr (attr ref)])

(defn- datetime-local-value [value]
  (cond
    (instance? js/Date value) (subs (.toISOString ^js/Date value) 0 16)
    (string? value) (subs value 0 (min 16 (count value)))
    :else value))

(defn set-current-load! [load]
  (deframe/route-assoc! :current-load

                        (-> load
                            (util/update-when :transport/carrier (partial process-ref :ductile/id))
                            (util/update-when :transport/transporter (partial process-ref :ductile/id))
                            ;; This field may already be a local datetime string when a load comes
                            ;; from the v1 table path, so normalize both dates and strings here.
                            (util/update-when :transport/scheduled-customer-delivery-datetime datetime-local-value)
                            ;; The edit form derives a boolean from the persisted timestamp only
                            ;; when the source map actually contains the finalized timestamp key.
                            (#(if (contains? % :transport/finalized-at)
                                (assoc % :transport/finalized? (boolean (:transport/finalized-at %)))
                                %))
                            (select-keys [:ductile/id :load/id
                                          :transport/carrier
                                          :transport/transporter
                                          :transport/finalized?
                                          :transport/status
                                          :transport/finalized-at
                                          :transport/scheduled-customer-delivery-datetime
                                          :transport/customer-load-number
                                          :transport/available-actions
                                          :transport/license-plate
                                          :transport/driver-name
                                          :ductile/note
                                          :transport/carrier-negotiated-amount
                                          :transport/customer-negotiated-amount]))))


(defn truck? [v]
  (contains? v :truck/number))

(defn transporter-or-carrier-choice [truck-or-carrier]
  {:ui/label (str (if (truck? truck-or-carrier)
                    (str (:truck/number truck-or-carrier) " (" (:address/name (:transport/carrier truck-or-carrier)) ")")
                    (:address/name truck-or-carrier))
                  (when-not (str/blank? (:carrier/code truck-or-carrier))
                    (str " (" (:carrier/code truck-or-carrier) ")")))
   :ui/value truck-or-carrier})

(defn transporter-or-carrier [{:keys [trucks carriers]} ref]
  (when ref
    (util/find-by-lookup-ref (concat trucks carriers) ref)))

(defn set-transporter-or-carrier! [value]
  (deframe/route-assoc!
   :current-load
   (merge (dissoc @!current-load :transport/transporter :transport/carrier :transport/license-plate :transport/driver-name)
          (select-keys value [:transport/license-plate])
          (if (truck? value)
            {:transport/transporter [:ductile/id (:ductile/id value)]
             :transport/driver-name (-> value :transport/driver :transport/driver-name)}
            {:transport/carrier [:ductile/id (:ductile/id value)]}))))

(defn transport-mode-choice [mode]
  {:ui/label (str/capitalize (tr mode)) :ui/value mode})

(defn transport-mode-select [{:transport/keys [modes]}]
  [:div
   [:label.mb-1.block.text-xs.text-slate-500 (tr "Transport Mode")]
   [:div {:data-testid "transport-mode"}
    [combobox/view
     {:variant :flex
      :placeholder (tr "Unassigned")
      :value (when-let [mode (:transport/mode @!current-load)]
               (transport-mode-choice mode))
      :choices (map transport-mode-choice modes)
      :on-clear #(deframe/route-assoc! :current-load (dissoc @!current-load
                                                             :transport/mode
                                                             :transport/name))
      :on-change (fn [{:ui/keys [value]}]
                   (when value
                     (deframe/route-assoc! :current-load (cond-> (assoc @!current-load :transport/mode value)
                                                           (= value :mode/truck) (dissoc :transport/name)))))}]]])

(defn transport-name-input []
  [:div
   [:label.mb-1.block.text-xs.text-slate-500 (tr "Transport name")]
   [:div {:data-testid "transport-name"}
    [ui/input
     {:on-change #(deframe/route-assoc-in! [:current-load :transport/name] (.. % -target -value))
      :on-clear #(deframe/route-dissoc-in! [:current-load :transport/name])
      :placeholder (tr "Unassigned")
      :variant :flex
      :value (:transport/name @!current-load)}]]])

(defn transporter-or-carrier-select [{:as params :keys [trucks carriers]}]
  (let [{:transport/keys [carrier transporter]} @!current-load]
    [:div
     [:label.mb-1.block.text-xs.text-slate-500 (tr "Truck or Carrier")]
     [:div {:data-testid "truck-or-carrier-select"}
      [combobox/view
       {:variant :flex
        :placeholder (tr "Unassigned")
        :value (when (or transporter carrier)
                 (transporter-or-carrier-choice (transporter-or-carrier params (or transporter carrier))))
        :choices (map transporter-or-carrier-choice (concat trucks carriers))
        :extra-keys [:address/name]
        :choice-view combobox/transporter-or-carrier-choice
        :on-clear #(deframe/route-assoc! :current-load (dissoc @!current-load
                                                               :transport/transporter
                                                               :transport/carrier
                                                               :transport/driver-name
                                                               :transport/license-plate
                                                               :override-license-plate?))
        :on-change (fn [{:ui/keys [value]}]
                     (when value
                       (set-transporter-or-carrier! value)))}]
      [:div.text-slate11.leading-normal.mt-1 {:class "text-[11px] mt-[3px] ml-2"}
       (tr "You can create carriers or edit existing carriers on the")
       " "
       [ui/link
        {:href (str "/compound/" (deframe/!get-in [:route :params :compound/name]) "/carriers")}
        (tr "carriers")]
       " "
       (tr "page") "."]]]))

(defn transporter-select [{:as params :keys [trucks]}]
  (let [{:transport/keys [transporter]} @!current-load]
    [:div
     [:label.mb-1.block (tr "Truck")]
     [:div {:data-testid "transporter-select"}
      [combobox/view
       {:placeholder (tr "Unassigned")
        :value (when transporter
                 (transporter-or-carrier-choice (transporter-or-carrier params transporter)))
        :choices (map transporter-or-carrier-choice trucks)
        :choice-view combobox/transporter-choice
        :on-clear #(deframe/route-assoc! :current-load (dissoc @!current-load :transport/transporter))
        :on-change (fn [{:ui/keys [value]}]
                     (when value
                       (set-transporter-or-carrier! value)))}]
      [:div.text-slate11.leading-normal.mt-1.mb-2 {:class "text-[11px] mt-[3px] ml-2"}
       (tr "You can create trucks or edit existing trucks on the")
       " "
       [ui/link
        {:href "/trucks"}
        (tr "Trucks")]
       " "
       (tr "page") "."]]]))

(defn carrier-select [{:as params :keys [carriers]}]
  (let [{:transport/keys [carrier]} @!current-load]
    [:div
     [:label.mb-1.block (tr "Carrier")]
     [:div.mb-1 {:data-testid "transporter-select"}
      [combobox/view
       {:placeholder (tr "Unassigned")
        :value (when carrier
                 (transporter-or-carrier-choice (transporter-or-carrier params carrier)))
        :choices (map transporter-or-carrier-choice carriers)
        :choice-view combobox/carrier-choice
        :on-clear #(deframe/route-assoc! :current-load (dissoc @!current-load :transport/carrier :override-license-plate?))
        :on-change (fn [{:ui/keys [value]}]
                     (when value
                       (set-transporter-or-carrier! value)))}]
      [:div.text-slate11.leading-normal.mt-1.mb-2 {:class "text-[11px] mt-[3px] ml-2"}
       (tr "You can create carriers or edit existing carriers on the")
       " "
       [ui/link
        {:href (str "/compound/" (deframe/!get-in [:route :params :compound/name]) "/carriers")}
        (tr "Carriers")]
       " "
       (tr "page") "."]]]))

(defn license-plate-input [params]
  (let [{:keys [override-license-plate?] :transport/keys [carrier transporter]} @!current-load
        {:transport/keys [license-plate]} (transporter-or-carrier params (or transporter carrier))
        !el (hooks/use-ref nil)
        override-license-plate? (or override-license-plate? (not= license-plate (:transport/license-plate @!current-load)))]
    [:div.w-full
     [:label.mb-1.flex.gap-1.text-xs.text-slate-500
      (tr "License Plate")]
     [:div {:data-testid "license-plate"}
      [ui/input
       {:on-change #(deframe/route-assoc-in! [:current-load :transport/license-plate] (.. % -target -value))
        :ref !el
        :placeholder (tr "Unassigned")
        :mask :ui.mask/license-plate
        :variant :flex
        :disabled? (and license-plate (not override-license-plate?))
        :value (:transport/license-plate @!current-load)}]
      (when license-plate
        [:span {:class ["text-[11px]"]}
         [ui/link
          {:on-click (fn toggle-override [event]
                       (if override-license-plate?
                         (deframe/route-assoc! :current-load (cond-> (dissoc @!current-load :transport/license-plate :override-license-plate?)
                                                               license-plate (assoc :transport/license-plate license-plate)))
                         (do (deframe/tx! [[:op/assoc-in [:current-route/transient :current-load :override-license-plate?] true]
                                           [:op/dissoc-in [:current-route/transient :current-load :transport/license-plate]]])
                             (js/console.log @!el)
                             (js/setTimeout #(do (.focus @!el) (.select @!el)) 50))))}
          (if override-license-plate?
            (tr "Restore saved")
            (tr "Override"))]])]]))

(defn driver-name-input [params]
  (let [{:keys [override-driver-name?] :transport/keys [carrier transporter]} @!current-load
        driver-name (-> (transporter-or-carrier params (or transporter carrier))
                        :transport/driver :transport/driver-name)
        !el (hooks/use-ref nil)
        override-driver-name? (or override-driver-name? (not= driver-name (:transport/driver-name @!current-load)))]
    [:div.w-full
     [:label.mb-1.flex.gap-1.text-xs.text-slate-500
      (tr "Driver")]
     [:div {:data-testid "driver-name"}
      [ui/input
       {:on-change #(deframe/route-assoc-in! [:current-load :transport/driver-name] (.. % -target -value))
        :ref !el
        :placeholder (tr "Unassigned")
        :variant :flex
        :disabled? (and driver-name (not override-driver-name?))
        :value (:transport/driver-name @!current-load)}]
      (when driver-name
        [:span {:class ["text-[11px]"]}
         [ui/link
          {:on-click (fn toggle-override [event]
                       (if override-driver-name?
                         (deframe/route-assoc! :current-load (cond-> (dissoc @!current-load :transport/driver-name :override-driver-name?)
                                                               driver-name (assoc :transport/driver-name driver-name)))
                         (do (deframe/route-assoc-in! [:current-load :override-driver-name?] true)
                             (js/console.log @!el)
                             (js/setTimeout #(do (.focus @!el) (.select @!el)) 50))))}
          (if override-driver-name?
            (tr "Restore saved")
            (tr "Override"))]])]]))

(defn customer-load-number-input []
  [:div
   [:label.mb-1.block.text-xs.text-slate-500
    (tr :transport/customer-load-number)]
   [:div {:data-testid "customer-load-number"}
    [ui/input
     {:on-change #(deframe/route-assoc-in! [:current-load :transport/customer-load-number] (.. % -target -value))
      :placeholder (tr "Unassigned")
      :variant :flex
      :value (:transport/customer-load-number @!current-load)}]]])

(defn note-input []
  [:div
   [:label.mb-1.block.text-xs.text-slate-500 (tr "Note")]
   [:div {:data-testid "transport-note"}
    [ui/input
     {:on-change #(deframe/route-assoc-in! [:current-load :ductile/note] (.. % -target -value))
      :placeholder (tr "Unassigned")
      :variant :flex
      :value (:ductile/note @!current-load)}]]])

(defn carrier-negotiated-amount-input []
  [:div.w-full
   [:label.mb-1.block.text-xs.text-slate-500 (tr :transport/carrier-negotiated-amount)]
   [:div {:data-testid "negotiated-amount"}
    [ui/input
     {:right "€"
      :mask :ui.mask/currency
      :on-change #(deframe/route-assoc-in! [:current-load :transport/carrier-negotiated-amount] (.. % -target -value))
      :placeholder (tr "n/a")
      :variant :flex
      :value (:transport/carrier-negotiated-amount @!current-load)}]]])

(defn customer-negotiated-amount-input []
  [:div.w-full
   [:label.mb-1.block.text-xs.text-slate-500 (tr :transport/customer-negotiated-amount)]
   [:div {:data-testid "customer-negotiated-amount"}
    [ui/input
     {:right "€"
      :mask :ui.mask/currency
      :on-change #(deframe/route-assoc-in! [:current-load :transport/customer-negotiated-amount] (.. % -target -value))
      :placeholder (tr "n/a")
      :variant :flex
      :value (:transport/customer-negotiated-amount @!current-load)}]]])

(defn finalized-toggle []
  [:label.flex.items-start.gap-3.cursor-pointer.select-none
   {:data-testid "finalized-toggle"}
   [:input.mt-0.5
    {:type "checkbox"
     :checked (boolean (:transport/finalized? @!current-load))
     :on-change #(deframe/route-assoc-in! [:current-load :transport/finalized?]
                                          (.. % -target -checked))}]
   [:div.flex.flex-col.gap-0.5
    [:span.text-xs.text-slate-500 (tr "Finalized?")]
    [:span.leading-normal.text-slate11 {:class ["text-[11px]"]}
     (if (:transport/finalized? @!current-load)
       (tr "The load is marked as handed over to the compound. If you remove “finalized” the load will revert into planning mode. The compound will be notified.")
       (tr "Right now the load is still marked as in planning. Marking it as finalized will hand it over to the compound to prepare the load."))]]])

(defn ensure-trucks-and-carriers! []
  (deframe/call-lambda {:result-path [:trucks]} 'ductile.transport/active-trucks (dissoc (deframe/current-route-params) :ductile/id))
  (deframe/call-lambda {:result-path [:carriers]} 'ductile.locations/reviewed-carriers (dissoc (deframe/current-route-params) :ductile/id)))

(defn carrier-summary [carrier]
  (str/join ", "
            (remove nil? [(:address/name carrier)
                          (when (or (:address/postcode carrier) (:address/city carrier))
                            (str/join " " [(:address/postcode carrier) (:address/city carrier)]))])))

(defn transporter-summary [transporter]
  (str/join ", "
            (remove nil? [(when (:truck/number transporter) (tr "Truck %1" (:truck/number transporter)))
                          (get-in transporter [:transport/carrier :address/name])
                          (:transport/license-plate transporter)
                          (-> transporter :transport/driver :transport/driver-name)])))

(defn transport-form [{:keys [on-submit fixed-params submit-button-class show-debug-output?]}]
  (hooks/use-effect ensure-trucks-and-carriers!)
  (let [params {:trucks @!trucks :carriers @!carriers}
        submittable-load (util/dissoc-blank-vals (merge @!current-load fixed-params))]
    [:<>
     (when-not (:transport/mode fixed-params)
       [transport-mode-select {:transport/modes [:mode/truck :mode/train :mode/ship :mode/own-wheels]}])
     (when (and (:transport/mode @!current-load)
                (not (contains? #{:mode/truck} (:transport/mode @!current-load))))
       [transport-name-input])
     #_[transporter-select params]
     #_[carrier-select params]
     [transporter-or-carrier-select params]
     [license-plate-input params]
     [driver-name-input params]
     [note-input]
     [:div
      {:class submit-button-class}
      [ui/button {:variant :primary
                  :on-click #(on-submit submittable-load)}
       (tr "Save transport")]]
     (when show-debug-output?
       [:pre.font-mono.mt-3.overflow-x-auto
        {:class "text-[11px]"}
        (with-out-str (pprint/pprint submittable-load))])]))

(defn edit-transport-dialog [{:keys [on-submit available-actions fixed-params show-debug-output? on-close]}]
  (hooks/use-effect (fn []
                      (ensure-trucks-and-carriers!)
                      (when-not @!current-load
                        (deframe/route-assoc! :current-load fixed-params))))
  (let [params {:trucks @!trucks :carriers @!carriers}
        load @(deframe/!route-get :current-load)
        new-load? (not (contains? load :ductile/id))
        submittable-load (cond-> (util/dissoc-blank-vals (merge @!current-load fixed-params))
                           (:load/id load)
                           (assoc :transport/carrier-negotiated-amount (:transport/carrier-negotiated-amount @!current-load)))]
    [ui/alert {:open? true
               :on-close on-close}
     [ui/alert-content
      [ui/alert-title
       (if new-load?
         (tr "Create new load")
         [:div.flex.items-center.gap-2
          [:span (str (tr "Edit load") " " (:load/id load))]
          (.log js/console load)
          (when-let [status (:transport/status load)]
            [ui/transport-status-badge status])])]
      (when-some [error @(deframe/!route-get-in [:fetch :error])]
        [:div.overflow-x-auto.p-2.bg-red-50.rounded-md.border.border-red-400.mb-2.flex-shrink-0
         [ui/expected-error-view error]])
      [ui/alert-description
       [:div.text-sm.flex.flex-col.gap-4
        (when-not (:transport/mode fixed-params)
          [transport-mode-select {:transport/modes [:mode/truck :mode/train :mode/ship :mode/own-wheels]}])
        (when (and (:transport/mode @!current-load)
                   (not (contains? #{:mode/truck} (:transport/mode @!current-load))))
          [transport-name-input])
        #_[transporter-select params]
        #_[carrier-select params]
        [transporter-or-carrier-select params]
        [:div.flex.gap-3.w-full
         [license-plate-input params]
         [driver-name-input params]]
        (when (:transport/can-be-finalized? available-actions)
          [finalized-toggle])

        [:div.flex.gap-3.w-full
         (when (:transport/can-set-scheduled-delivery-datetime? available-actions)
           [:div.w-full
            [:label.text-xs.mb-1.block.text-slate-500
             (tr :transport/scheduled-customer-delivery-datetime)]
            [ui/native-datetime-picker nil
             (:transport/scheduled-customer-delivery-datetime @!current-load)
             #(deframe/route-assoc-in! [:current-load :transport/scheduled-customer-delivery-datetime] %)]])

         (when (and (:transport/can-set-negotiated-amount? available-actions) (util/has-required-write-scope? :scope/transport-customer-price))
           [customer-negotiated-amount-input])
         (when (:transport/can-set-negotiated-amount? available-actions)
           [carrier-negotiated-amount-input])]
        (when (:transport/can-set-customer-load-number? available-actions)
          [customer-load-number-input])
        [note-input]
        (when show-debug-output?
          [:pre.font-mono.mt-3.overflow-x-auto
           {:class "text-[11px]"}
           (with-out-str (pprint/pprint submittable-load))])]
       #_[:div.mb-3 (tr "The following %1 vehicles will be removed from the load:" (count (:waypoint/transports waypoint)))]
       #_[transport/waypoint-vehicle-list (dissoc waypoint :waypoint/editable?)]]
      [:div {:class "flex justify-end gap-3"}
       [ui/alert-cancel
        [ui/button
         {:on-click on-close}
         (tr "Cancel")]]
       [ui/alert-action
        [ui/button
         {:variant :primary
          :data-testid "submit-load"
          :on-click #(on-submit submittable-load)}
         [ui/icon {:size 16} :check]
         (if new-load?
           (tr "Save new load")
           (tr "Update load"))]]]]]))
